import { useNavigate, Link } from 'react-router-dom'
import SearchBar from '@/components/SearchBar'
import CategoryCard from '@/components/CategoryCard'
import ProductCard from '@/components/ProductCard'
import Skeleton from '@/components/Skeleton'
import { useCategories } from '@/hooks/useCategories'
import { useProducts } from '@/hooks/useProducts'
import { useOrg } from '@/context/OrgContext'

const SECTION_CONFIG = [
  { tipe: 'jual'  as const, label: 'Rekomendasi',  dot: 'bg-blue-600',    route: null         },
  { tipe: 'sewa'  as const, label: 'Produk Sewa',  dot: 'bg-purple-600',  route: '/sewa'      },
  { tipe: 'jasa'  as const, label: 'Layanan',       dot: 'bg-emerald-600', route: '/jasa'      },
  { tipe: 'tour'  as const, label: 'Paket Tour',    dot: 'bg-orange-500',  route: '/tour'      },
]

function ProductSection({
  label, dot, route, products, loading,
}: {
  label: string
  dot: string
  route: string | null
  products: ReturnType<typeof useProducts>['products']
  loading: boolean
}) {
  if (!loading && products.length === 0) return null

  return (
    <section className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dot}`} />
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</h2>
        </div>
        {route && products.length > 0 && (
          <Link to={route} className="text-xs text-blue-600 hover:underline font-medium">
            Lihat semua →
          </Link>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-52" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {products.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </section>
  )
}

export default function Home() {
  const navigate = useNavigate()
  const { isRental, isJasa, isTour } = useOrg()
  const { categories, loading: catLoading } = useCategories()

  const { products: jualProducts,  loading: jualLoading  } = useProducts('jual')
  const { products: sewaProducts,  loading: sewaLoading  } = useProducts(isRental ? 'sewa'  : undefined)
  const { products: jasaProducts,  loading: jasaLoading  } = useProducts(isJasa   ? 'jasa'  : undefined)
  const { products: tourProducts,  loading: tourLoading  } = useProducts(isTour   ? 'tour'  : undefined)

  const sections = [
    { ...SECTION_CONFIG[0], products: jualProducts, loading: jualLoading,  show: true      },
    { ...SECTION_CONFIG[1], products: sewaProducts, loading: sewaLoading,  show: isRental  },
    { ...SECTION_CONFIG[2], products: jasaProducts, loading: jasaLoading,  show: isJasa    },
    { ...SECTION_CONFIG[3], products: tourProducts, loading: tourLoading,  show: isTour    },
  ]

  return (
    <div className="pb-8">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <SearchBar onSearch={q => navigate(`/search?q=${encodeURIComponent(q)}`)} />
      </div>

      <section className="max-w-7xl mx-auto px-4 py-2">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Kategori</h2>
        {catLoading ? (
          <div className="flex gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <Skeleton className="w-16 h-16 rounded-full" />
                <Skeleton className="w-12 h-3" />
              </div>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <p className="text-sm text-gray-400">Belum ada kategori.</p>
        ) : (
          <div className="flex gap-1 overflow-x-auto pb-1 md:flex-wrap">
            {categories.map(cat => (
              <div key={cat.id} className="shrink-0">
                <CategoryCard category={cat} />
              </div>
            ))}
          </div>
        )}
      </section>

      {sections.filter(s => s.show).map(s => (
        <ProductSection
          key={s.tipe}
          label={s.label}
          dot={s.dot}
          route={s.route}
          products={s.products}
          loading={s.loading}
        />
      ))}
    </div>
  )
}
